// The image + video slots every piece of content shares.
//
// One image (Supabase Storage, public — cover art, not gated content) and one video
// (Mux). Video arrives two ways: a file uploaded straight to Mux's direct-upload URL,
// or an existing playback id pasted in. Uploads are asynchronous — Mux transcodes after
// the PUT — so the slot polls until the webhook flips the row to `ready`; publishing is
// blocked server-side until it does.
//
// The component edits *draft ids* via onChange; the parent form owns persistence, the
// same as every other field.

import { useEffect, useRef, useState } from 'react';
import { useApp } from '../lib/app-context';
import type { MediaAsset, Uuid } from '../lib/types';
import { Badge, Field } from './ui';

const POLL_MS = 3000;

export function MediaSlots({ moduleId, imageId, videoId, onImage, onVideo }: {
    moduleId: Uuid;
    imageId: Uuid | null | undefined;
    videoId: Uuid | null | undefined;
    onImage: (id: Uuid | null) => void;
    onVideo: (id: Uuid | null) => void;
}) {
    return (
        <div className="field-row">
            <ImageSlot moduleId={moduleId} mediaId={imageId ?? null} onChange={onImage} />
            <VideoSlot moduleId={moduleId} mediaId={videoId ?? null} onChange={onVideo} />
        </div>
    );
}

function ImageSlot({ moduleId, mediaId, onChange }: {
    moduleId: Uuid; mediaId: Uuid | null; onChange: (id: Uuid | null) => void;
}) {
    const { data } = useApp();
    const [asset, setAsset] = useState<MediaAsset | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!mediaId) { setAsset(null); return; }
        data.getMedia(mediaId).then(setAsset).catch(() => setAsset(null));
    }, [data, mediaId]);

    async function upload(file: File) {
        setBusy(true);
        setError(null);
        try {
            const row = await data.uploadImage(moduleId, file);
            onChange(row.id);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Field label="Image">
            <div className="stack" style={{ gap: 8 }}>
                {error && <div className="badge error" title={error}>{error}</div>}
                {asset?.storage_path && (
                    <img
                        src={data.imageUrl(asset.storage_path)}
                        alt={asset.title}
                        style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                    />
                )}
                <div className="row-tight">
                    <input ref={input} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
                    <button className="btn small" disabled={busy} onClick={() => input.current?.click()}>
                        {busy ? 'Uploading…' : asset ? 'Replace image' : 'Upload image'}
                    </button>
                    {asset && (
                        <button className="btn small" onClick={() => onChange(null)}>Remove</button>
                    )}
                </div>
            </div>
        </Field>
    );
}

function VideoSlot({ moduleId, mediaId, onChange }: {
    moduleId: Uuid; mediaId: Uuid | null; onChange: (id: Uuid | null) => void;
}) {
    const { data } = useApp();
    const [asset, setAsset] = useState<MediaAsset | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pastedId, setPastedId] = useState('');
    const input = useRef<HTMLInputElement>(null);

    // While the transcode runs, keep looking until the webhook lands `ready`.
    useEffect(() => {
        if (!mediaId) { setAsset(null); return; }
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        const look = async () => {
            const row = await data.getMedia(mediaId).catch(() => null);
            if (cancelled) return;
            setAsset(row);
            if (row && (row.status === 'uploading' || row.status === 'processing')) {
                timer = setTimeout(look, POLL_MS);
            }
        };
        look();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [data, mediaId]);

    async function uploadFile(file: File) {
        setBusy('Starting upload…');
        setError(null);
        try {
            const { mediaId: id, uploadUrl } = await data.createVideoUpload(moduleId, file.name);
            setBusy('Uploading to Mux…');
            const put = await fetch(uploadUrl, { method: 'PUT', body: file }).catch(() => null);
            // The mock's about:blank URL rejects; the row still becomes ready.
            if (put && !put.ok && !uploadUrl.startsWith('about:')) {
                throw new Error(`Upload failed (${put.status})`);
            }
            onChange(id);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    }

    async function usePasted() {
        if (!pastedId.trim()) return;
        setBusy('Registering…');
        setError(null);
        try {
            const row = await data.registerMuxPlayback(moduleId, pastedId, 'Existing Mux video');
            setPastedId('');
            onChange(row.id);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    }

    const status = asset?.status;

    return (
        <Field label="Video">
            <div className="stack" style={{ gap: 8 }}>
                {error && <div className="badge error" title={error}>{error}</div>}
                {asset && (
                    <div className="row-tight">
                        <Badge tone={status === 'ready' ? 'teal' : status === 'errored' ? 'error' : 'neutral'}>
                            {status === 'ready' ? 'Ready'
                                : status === 'errored' ? 'Errored'
                                : 'Processing…'}
                        </Badge>
                        {asset.mux_playback_id && <code className="mono">{asset.mux_playback_id}</code>}
                    </div>
                )}
                <div className="row-tight">
                    <input ref={input} type="file" accept="video/*" style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
                    <button className="btn small" disabled={busy !== null} onClick={() => input.current?.click()}>
                        {busy ?? (asset ? 'Replace video' : 'Upload video')}
                    </button>
                    {asset && (
                        <button className="btn small" onClick={() => onChange(null)}>Remove</button>
                    )}
                </div>
                {!asset && (
                    <div className="row-tight">
                        <input
                            className="input"
                            placeholder="…or paste a Mux playback ID"
                            value={pastedId}
                            onChange={(e) => setPastedId(e.target.value)}
                            style={{ maxWidth: 220 }}
                        />
                        <button className="btn small" disabled={!pastedId.trim() || busy !== null}
                            onClick={usePasted}>Use</button>
                    </div>
                )}
            </div>
        </Field>
    );
}
